<i18n lang="yaml">
pt:
  recent: 'Aberto recentemente'
  noRecent: 'Nenhum item aberto recentemente'
en:
  recent: 'Recently opened'
  noRecent: 'No recent items'
</i18n>

<template>
  <div>
    <v-list-subheader class="search-input-recent__subheader">
      {{ t('recent') }}
    </v-list-subheader>
    <SearchableItemsList
      v-for="(items, type) in recentItems"
      :key="type"
      :items="items"
      :truncate-at="3"
      @item-clicked="$emit('itemClicked')"
    />
    <v-list
      v-if="emptyRecent"
      density="compact"
    >
      <v-list-subheader>
        {{ t('noRecent') }}
      </v-list-subheader>
    </v-list>
  </div>
</template>

<script lang="ts">
import { mapGetters, mapState } from '~/assets/javascript/modules/vuex';
import { getRecentNavigation, SearchableItemBuilder, ViewItemBuilder, SheetItemBuilder, WorkflowItemBuilder } from '~/assets/javascript/utils';
import SearchableItemsList from '../_searchable-items-list';
import ROUTES from '~/assets/javascript/constants/routes';

const RECENT_ITEM_TYPES = Object.freeze(['views', 'workflows', 'sheets']);

export default defineComponent({
  name: 'SearchInputRecent',
  components: {
    SearchableItemsList,
  },
  emits: ['itemClicked'],
  setup() {
    return {
      t: useI18n().t,
      ...mapState('environment', ['currentEnvironment']),
      ...mapState('builderView', { builderViews: 'views' }),
      ...mapState('viewsMenu', { viewsMenu: 'views' }),
      ...mapState('viewOptions', { sheets: 'sheetsCache' }),
      ...mapState('workflows', ['workflows']),
      ...mapGetters('workspace', ['allSystemHiddenViewIds']),
    };
  },
  computed: {
    searchableResourcesBuilders() {
      const builderParams = {
        $localeRoute: this.$localeRoute,
        $route: this.$route,
        currentEnvironment: this.currentEnvironment,
        $i18n: this.$i18n,
      };
      return {
        view: new ViewItemBuilder(builderParams),
        sheet: new SheetItemBuilder(builderParams),
        workflow: new WorkflowItemBuilder(builderParams),
      };
    },
    searchableItemBuilder() {
      return Object.keys(this.searchableResourcesBuilders).reduce((acc, key) => {
        acc[key] = new SearchableItemBuilder(this.searchableResourcesBuilders[key]);
        return acc;
      }, {});
    },
    views() {
      return this.currentEnvironment === 'studio' ? this.builderViews : this.viewsMenu;
    },
    emptyRecent() {
      return _isEmpty(this.recentNavigation) || Object.values(this.recentNavigation).every(_isEmpty);
    },
    recentNavigation() {
      return RECENT_ITEM_TYPES.reduce((acc, key) => {
        let resources = getRecentNavigation(this.currentEnvironment, key) || [];

        if (this.currentEnvironment === 'member' && key === 'views') {
          resources = resources.filter((routeData) => {
            const id = routeData.params[ROUTES.member.view.viewId];
            return !this.allSystemHiddenViewIds.includes(id);
          });
        }

        acc[key] = resources;
        return acc;
      }, {});
    },
    recentItems() {
      return RECENT_ITEM_TYPES.reduce((accumulator, key) => {
        accumulator[key] = this.searchableItemBuilder[key.replace(/s$/, '')].buildItems(this.getItems(key));

        return accumulator;
      }, {});
    },
  },
  methods: {
    getItems(type) {
      return this[type].filter(({ id }) => this.recentNavigation[type].some(route => [route.params.viewId, route.params.id].includes(id)));
    },
  },
});
</script>

<style lang="scss" scoped>
.search-input-recent__subheader {
  &.v-list-subheader {
    font-size: 12px;
    font-weight: 500;
    height: 20px;
    margin-left: 8px;
    margin-top: 8px;
    padding: 0;
  }
}
</style>
